import { Component, input } from '@angular/core';

import { FormField, type FieldTree } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

// Canonical dimension fields shared by PartDimensions and ComponentDimensions (see
// dataconnect/schema/part.gql and dataconnect/schema/component.gql) — both tables use this
// exact same 8-Float-column shape, so this model needs no per-consumer field remapping.
export interface DimensionsFieldsValue {
  dod: number | null;
  did: number | null;
  drift: number | null;
  lengthClosed: number | null;
  lengthOpen: number | null;
  workingLength: number | null;
  overallLength: number | null;
  tareWeight: number | null;
}

export function emptyDimensionsFieldsValue(): DimensionsFieldsValue {
  return {
    dod: null,
    did: null,
    drift: null,
    lengthClosed: null,
    lengthOpen: null,
    workingLength: null,
    overallLength: null,
    tareWeight: null,
  };
}

// Shared mapper for anything shaped like a PartDimensions/ComponentDimensions row (both tables
// use this exact 8-Float-column shape) into the canonical view model, so each caller doesn't
// hand-roll the same `?? null` field-by-field mapping.
export function toDimensionsFieldsValue(source: Partial<DimensionsFieldsValue> | null | undefined): DimensionsFieldsValue | null {
  if (!source) return null;
  return {
    dod: source.dod ?? null,
    did: source.did ?? null,
    drift: source.drift ?? null,
    lengthClosed: source.lengthClosed ?? null,
    lengthOpen: source.lengthOpen ?? null,
    workingLength: source.workingLength ?? null,
    overallLength: source.overallLength ?? null,
    tareWeight: source.tareWeight ?? null,
  };
}

// "Sync" merge: fills in only fields missing on `target` from `fallback` — target is always the
// source of truth once a field is set. Uses `??`, not `||`, so a real `0` measurement counts as
// already-set and is never overwritten.
export function mergeDimensionsFields(
  target: DimensionsFieldsValue | null,
  fallback: Partial<DimensionsFieldsValue> | null | undefined,
): DimensionsFieldsValue {
  const source = toDimensionsFieldsValue(fallback) ?? emptyDimensionsFieldsValue();
  return {
    dod: target?.dod ?? source.dod,
    did: target?.did ?? source.did,
    drift: target?.drift ?? source.drift,
    lengthClosed: target?.lengthClosed ?? source.lengthClosed,
    lengthOpen: target?.lengthOpen ?? source.lengthOpen,
    workingLength: target?.workingLength ?? source.workingLength,
    overallLength: target?.overallLength ?? source.overallLength,
    tareWeight: target?.tareWeight ?? source.tareWeight,
  };
}

// Presentational only — view-mode rows or edit-mode inputs for the 8 shared dimension fields.
// No card/header chrome and no save/mutation logic: callers own their own mat-card, Edit/Save/
// Cancel buttons, and Signal Forms `form()`/mutation call, since what else lives alongside these
// fields (and what saving them means) differs per caller — see Part Details vs Component Info.
@Component({
  selector: 'dimensions-fields',
  templateUrl: './dimensions-fields.component.html',
  styleUrl: './dimensions-fields.component.scss',
  imports: [FormField, MatFormFieldModule, MatInputModule],
})
export class DimensionsFieldsComponent {
  dimensions = input<DimensionsFieldsValue | null>(null);
  editing = input(false);
  form = input<FieldTree<DimensionsFieldsValue> | null>(null);
}
